import React from 'react';
import { Grid, Box } from '@mui/material';
import { WorkerProfileField } from 'types/profileCustomFields';

import TextField from 'modules/views/shared/TextField';
import MobileInput from 'modules/views/shared/MobileInput';
import SkippableFieldIndicator from 'modules/views/shared/SkippableFieldIndicator';

import { IContactInfoConfig } from '../hooks/useSectionsConfig';

interface IContactInfoSectionProps {
  config: IContactInfoConfig;
  contactForm: {
    email: string;
    mobileNumber: string;
    phoneNumber: string;
  };
  formErrors: {
    email?: { message: string };
    mobileNumber?: { message: string };
    phoneNumber?: { message: string };
  };
  onFieldChange: (field: WorkerProfileField, value: string) => void;
  onPhoneChange: (field: WorkerProfileField) => (value: { phone: string; inputValue: string; country: any }) => void;
  canEditEmail: boolean;
  canEditMobilePhoneNumber: boolean;
  isSubmitting: boolean;
  sectionClasses: string;
  sectionTitleClasses: string;
  labels: {
    email: string;
    mobileNumber: string;
    alternatePhone: string;
    contactInfoTitle: string;
  };
  workerProfileFields: {
    email: WorkerProfileField;
    mobilePhoneNumber: WorkerProfileField;
    alternateContactMobilePhoneNumber: WorkerProfileField;
  };
  isArchivedProject?: boolean;
}

const ContactInfoSection: React.FC<IContactInfoSectionProps> = ({
  config,
  contactForm,
  formErrors,
  onFieldChange,
  onPhoneChange,
  canEditEmail,
  canEditMobilePhoneNumber,
  isSubmitting,
  sectionClasses,
  sectionTitleClasses,
  labels,
  workerProfileFields,
  isArchivedProject,
}) => {
  // Remove useTranslation - using centralized labels instead

  if (!config.isVisible) return null;

  return (
    <Grid item xs={12} className={sectionClasses}>
      <p className={sectionTitleClasses}>{labels.contactInfoTitle}</p>
      <Grid container spacing={3}>
        {config.email.isVisible && (
          <Grid item xs={12} sm={6}>
            <TextField
              label={labels.email}
              required={config.email.isMandatory || (config.isEitherRequired && canEditMobilePhoneNumber)}
              name="email"
              testId="email-input"
              value={contactForm.email}
              error={formErrors.email?.message || ''}
              disabled={!canEditEmail || isArchivedProject}
              InputProps={{ readOnly: !canEditEmail || isSubmitting }}
              onChange={
                canEditEmail && !isSubmitting
                  ? e => onFieldChange(workerProfileFields.email, e.target.value)
                  : undefined
              }
            />
          </Grid>
        )}
        {config.phone.isVisible && (
          <Grid item xs={12} sm={6}>
            <MobileInput
              label={labels.mobileNumber}
              required={config.phone.isMandatory || (config.isEitherRequired && canEditEmail)}
              placeholder={labels.mobileNumber}
              testId="mobileNumber-input"
              value={contactForm.mobileNumber}
              error={formErrors.mobileNumber?.message || ''}
              disabled={!canEditMobilePhoneNumber || isArchivedProject}
              readOnly={isSubmitting}
              onChange={
                canEditMobilePhoneNumber && !isSubmitting
                  ? onPhoneChange(workerProfileFields.mobilePhoneNumber)
                  : () => {}
              }
            />
          </Grid>
        )}
        {config.alternatePhone.isVisible && (
          <Grid item xs={12} sm={6}>
            <Box>
              <MobileInput
                label={labels.alternatePhone}
                required={config.alternatePhone.isMandatory}
                placeholder={labels.alternatePhone}
                testId="phoneNumber-input"
                value={contactForm.phoneNumber}
                error={formErrors.phoneNumber?.message || ''}
                disabled={isArchivedProject}
                readOnly={isSubmitting}
                onChange={
                  !isSubmitting ? onPhoneChange(workerProfileFields.alternateContactMobilePhoneNumber) : () => {}
                }
              />
              <SkippableFieldIndicator show={config.alternatePhone.showSkippableIndicator || false} />
            </Box>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default ContactInfoSection;
